"use client";

import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Construction() {
  const platforms = [{
    name: "Fundamenty"
  }, {
    name: "Stan Surowy"
  }, {
    name: "Stan Deweloperski"
  }, {
    name: "Roboty Ziemne"
  }, {
    name: "Niwelacja"
  }, {
    name: "Przyłącza"
  }, {
    name: "Instalacje"
  }, {
    name: "Garaże"
  }, {
    name: "Ogrodzenia"
  }, {
    name: "Kostka Brukowa"
  }, {
    name: "Parkingi"
  }, {
    name: "Tarasy"
  }, {
    name: "Opaski"
  }, {
    name: "Mała Architektura"
  }, {
    name: "Zagospodarowanie"
  }];
  const markers = [{
    top: "32%",
    left: "18%",
    label: "Katowice"
  }, {
    top: "48%",
    left: "28%",
    label: "Gliwice"
  }, {
    top: "62%",
    left: "32%",
    label: "Zabrze"
  }, {
    top: "30%",
    left: "48%",
    label: "Bytom"
  }, {
    top: "52%",
    left: "52%",
    label: "Chorzów"
  }, {
    top: "38%",
    left: "62%",
    label: "Sosnowiec"
  }, {
    top: "42%",
    left: "72%",
    label: "Dąbrowa"
  }, {
    top: "58%",
    left: "78%",
    label: "Tychy"
  }, {
    top: "68%",
    left: "82%",
    label: "Rybnik"
  }, {
    top: "26%",
    left: "82%",
    label: "Bielsko"
  }, {
    top: "34%",
    left: "32%",
    label: "Jaworzno"
  }, {
    top: "44%",
    left: "44%",
    label: "Mysłowice"
  }];
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const mosaic = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const map = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const closing = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="3296" id="obserwuj-nasze-budowy" className="dark bg-background text-foreground py-24 md:py-32 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-3 lg:pt-4">
            <span className="font-default text-xs tracking-[0.25em] uppercase text-muted-foreground">
              BudSys — Realizacje
            </span>
          </div>
          <div className="lg:col-span-9">
            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              Gdzie powstaje <em className="text-primary font-light not-italic">jakość.</em>
            </h2>
          </div>
        </div>

        {}
        <div ref={intro.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-20 md:mb-28 transition-all duration-1000 delay-150 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-6 lg:col-start-2">
            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground">
              Każda inwestycja realizowana przez BudSys to połączenie precyzji inżynieryjnej z solidnością wykonania. Od fundamentów po zagospodarowanie terenu, dbamy o każdy detal, aby Twój dom był bezpieczny i trwały przez lata. Działamy na terenie całego województwa śląskiego.
            </p>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 lg:pl-8 lg:border-l lg:border-border">
            <p className="font-default text-2xl md:text-3xl leading-snug italic text-chart-3">
              "Kompleksowe wsparcie budowlane: od projektu po gotowy ogród."
            </p>
          </div>
        </div>

        {}
        <div ref={mosaic.ref} className={`mb-24 md:mb-32 transition-all duration-1000 ease-out ${mosaic.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-border/60 border border-border/60 rounded-lg overflow-hidden">
            {platforms.map((platform, i) => <div data-index={i} key={platform.name} className="group bg-background hover:bg-accent transition-colors duration-500 cursor-pointer flex items-center justify-center p-8 md:p-10 min-h-[120px] md:min-h-[140px]">
                <span className="font-default text-lg md:text-xl tracking-wide text-foreground group-hover:text-primary transition-colors duration-500 font-medium">
                  {platform.name}
                </span>
              </div>)}
          </div>
        </div>

        {}
        <div ref={map.ref} className={`mb-20 md:mb-28 transition-all duration-1000 ease-out ${map.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-3">
              <p className="font-default text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">
                Obszar Działania
              </p>
              <p className="font-default text-xl md:text-2xl leading-snug text-foreground">
                Solidne fundamenty <br />
                <em className="text-chart-3 font-light not-italic">na Śląsku.</em>
              </p>
            </div>

            <div className="lg:col-span-9 relative">
              <div className="relative aspect-[16/9] w-full">
                <svg viewBox="0 0 1000 500" className="w-full h-full" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  {}
                  <path d="M120 140 Q150 110 200 120 L260 130 Q290 145 310 175 L300 220 Q280 240 240 235 L180 230 Q140 220 130 190 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M240 245 Q260 255 280 270 L290 290 Q280 300 260 295 L240 280 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M290 300 Q320 310 340 340 L350 400 Q340 440 310 450 L290 430 Q280 380 285 340 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M460 130 Q500 120 540 130 L560 160 Q545 180 510 180 L470 170 Q455 155 460 140 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M480 200 Q520 195 555 215 L575 280 Q560 340 530 360 L500 350 Q480 310 475 260 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M580 170 Q620 165 650 185 L645 220 Q620 235 595 225 L580 200 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M660 140 Q720 125 790 135 L840 165 Q855 200 830 230 L760 235 Q700 225 670 200 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M780 250 Q810 255 830 275 L825 305 Q800 315 775 305 L770 275 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M810 350 Q850 345 880 365 L875 395 Q840 410 810 395 L805 370 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                  {}
                  <path d="M870 175 Q885 170 895 185 L890 210 Q875 215 868 200 Z" stroke="var(--muted-foreground)" strokeWidth="1" strokeOpacity="0.5" />
                </svg>

                {}
                {markers.map((m, i) => <div data-index={i} key={i} className="absolute -translate-x-1/2 -translate-y-1/2" style={{
                top: m.top,
                left: m.left
              }} aria-label={m.label}>
                    <span className="relative flex items-center justify-center">
                      <span className="absolute inline-flex h-4 w-4 rounded-lg bg-[#FFC11A] opacity-50 animate-ping" style={{
                    animationDuration: "2.8s"
                  }}></span>
                      <span className="relative inline-flex h-2 w-2 rounded-lg bg-destructive"></span>
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={closing.ref} className={`max-w-3xl mx-auto text-center transition-all duration-1000 ease-out ${closing.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground mb-10">
            Nasza filozofia budowania opiera się na pełnym zaangażowaniu i dbałości o standardy. Nie szukamy dróg na skróty — budujemy zaufanie poprzez rzetelność i terminowość. Każdy projekt traktujemy indywidualnie, zapewniając profesjonalne doradztwo na każdym etapie inwestycji.
          </p>

          <div className="flex justify-center">
            <Button asChild variant="outline" size="lg" className="rounded-lg border-foreground/40 bg-transparent text-foreground hover:bg-foreground hover:text-background px-8 h-12 font-default tracking-wide transition-colors duration-500">
              <Link to="/ContactPage">
                <MapPin className="w-4 h-4" />
                Zamów bezpłatną wycenę
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}